import React, { useState, useCallback, useMemo } from 'react';
import { Volume2, Clock, Cpu, Languages, AlertTriangle } from 'lucide-react';
import type { AudioGenerationRequest, Language, TTSProvider } from '../types';
import { DURATION_OPTIONS, LLM_PROVIDERS } from '../constants';
import { VoiceService } from '../services';
import { useLLMModels } from '@/hooks/useLLMModels';
import { useToast } from '@/hooks/useToast';
import { LLMModelSelector, TTSSourceSelector } from './selectors';
import { CompactSelect } from './common/CompactSelect';
import type { CompactOption } from './common/CompactSelect';
import { SegmentedPicker } from './common/SegmentedPicker';
import type { SegmentOption } from './common/SegmentedPicker';
import { AudioLanguageSelector, type AudioLanguageConfig } from './common/LanguageSelector';
import { TokenEstimateCard } from './tokens/TokenEstimateCard';
import { isAlwaysFreeTtsSource } from '../constants/ttsSources';
import { useLocalTtsInstallStatus } from '@/hooks/useLocalTtsInstallStatus';
import sharedConstants from '../constants/shared_constants.json';
import { getAllNLLBLanguages } from '../utils/languageMapping';
import { resolveInitialLanguage, persistLanguages } from '../utils/browserLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { COST_GATE_OPEN, costGateBlockLabel, type CostGateStatus } from '../utils/costGate';

const NLLB_CODES: ReadonlySet<string> = new Set(getAllNLLBLanguages().map((l) => l.code));

// Extract narration validation constants from shared constants
const USER_NARRATION_MAX_LENGTH = sharedConstants.constants.limits
  .USER_NARRATION_MAX_LENGTH as number;
const USER_NARRATION_MIN_LENGTH = sharedConstants.constants.limits
  .USER_NARRATION_MIN_LENGTH as number;

// Filter DURATION_OPTIONS for audio (5-300 seconds range)
const AUDIO_DURATION_OPTIONS = DURATION_OPTIONS.filter(
  (option) => option.seconds >= 5 && option.seconds <= 300
);

interface AudioConfigFormProps {
  onSubmit: (config: AudioGenerationRequest) => void;
  onPreviewScript?: (config: AudioGenerationRequest & { topic: string }) => void;
  disabled?: boolean;
  isPreviewLoading?: boolean;
}

export const AudioConfigForm: React.FC<AudioConfigFormProps> = ({
  onSubmit,
  onPreviewScript,
  disabled = false,
  isPreviewLoading = false,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [mode, setMode] = useState<'topic' | 'text'>('topic');
  const [topic, setTopic] = useState('');
  const [durationSeconds, setDurationSeconds] = useState(15);
  const [narrationText, setNarrationText] = useState('');
  // Multiple audio language tracks with configuration.
  // Seeded on first load: localStorage wins, then browser-detect, then 'en'.
  // A default provider + voice are resolved immediately (same logic as adding a
  // language later), so every language that has a provider voice starts with one
  // selected instead of an empty voice dropdown on first load.
  const [audioLanguages, setAudioLanguages] = useState<AudioLanguageConfig[]>(() => {
    const code = resolveInitialLanguage('audio', NLLB_CODES);
    const provider = VoiceService.getDefaultProviderForLanguage(code);
    const voice_id = VoiceService.getDefaultVoice(code as Language, provider as TTSProvider);
    return [{ language: code, provider, voice_id }];
  });
  const [ttsProvider, setTtsProvider] = useState<string>(
    () => audioLanguages[0]?.provider ?? 'kokoro'
  );
  const [language, setLanguage] = useState<string>(() => audioLanguages[0]?.language ?? 'en-us');
  const [voiceId, setVoiceId] = useState<string>(() => audioLanguages[0]?.voice_id ?? '');
  const [llmProvider, setLlmProvider] = useState<string>('openrouter');
  // Token billing state
  const [costGate, setCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);

  // Real, per-provider install state -- same source of truth as
  // TTSSourceSelector.tsx (which renders on this same form) and the
  // ImageConfigForm.tsx `installed` contract. Kokoro and F5-TTS have real
  // ComponentManager-backed installs (utils/components/kokoro_tts_manager.py,
  // utils/components/f5_tts_manager.py); chatterbox/higgs have none yet, so
  // they default to NOT installed (RULE #5: no fallback to "capability" --
  // see constants/pricing/tts.py is_tts_provider_free(), whose
  // GPU_TTS_PROVIDERS already lists f5_tts as free-once-installed).
  const ttsInstalled: Record<string, boolean> = useLocalTtsInstallStatus();

  // Whether THIS request is billed free right now: edge_tts/pyttsx3 are free
  // on every platform (isAlwaysFreeTtsSource); kokoro is free only when
  // actually installed; every other provider is never free by default. This
  // is the single signal fed to both the estimate request (`installed`) and
  // TokenEstimateCard's `isLocalProvider` override, so the standalone Audio
  // tab and the Video pipeline cost card (constants/pricing/pipeline.py)
  // agree on the price for the SAME selected provider (regression: 2026-08 --
  // Chatterbox showed "Free" here and the cloud rate on the pipeline card).
  const isProviderFreeNow = useMemo(() => {
    if (isAlwaysFreeTtsSource(ttsProvider)) return true;
    // Any provider with a local component is free exactly when it is installed;
    // one without one (chatterbox, higgs, every cloud provider) never is.
    return ttsInstalled[ttsProvider] === true;
  }, [ttsProvider, ttsInstalled]);

  // Calculate token estimate parameters (all providers cost tokens)
  const tokenEstimateParams = useMemo(() => {
    if (mode === 'topic') {
      return {
        duration_seconds: durationSeconds,
        provider: ttsProvider,
        installed: isProviderFreeNow,
      };
    } else {
      return {
        character_count: narrationText.length,
        provider: ttsProvider,
        text: narrationText,
        installed: isProviderFreeNow,
      };
    }
  }, [mode, durationSeconds, narrationText, ttsProvider, isProviderFreeNow]);

  // Fetch available models when LLM provider changes (only for topic mode)
  const {
    availableModels,
    modelsError,
    selectedModel: llmModel,
    setSelectedModel: setLlmModel,
  } = useLLMModels(llmProvider, undefined, { enabled: mode === 'topic' });

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      // Validate text mode requirements
      if (mode === 'text') {
        if (narrationText.trim().length < USER_NARRATION_MIN_LENGTH) {
          showToast(
            'error',
            t('audioConfigForm.validationError'),
            t('audioConfigForm.minLengthError', { min: USER_NARRATION_MIN_LENGTH })
          );
          return;
        }
        if (narrationText.length > USER_NARRATION_MAX_LENGTH) {
          showToast(
            'error',
            t('audioConfigForm.validationError'),
            t('audioConfigForm.maxLengthError', { max: USER_NARRATION_MAX_LENGTH.toLocaleString() })
          );
          return;
        }
      }

      const config: AudioGenerationRequest = {
        mode,
        tts_provider: ttsProvider,
        language,
        voice_id: voiceId,
      };

      if (mode === 'topic') {
        config.topic = topic;
        config.duration_seconds = durationSeconds;
        config.llm_provider = llmProvider;
        config.llm_model = llmModel;
      } else {
        config.narration_text = narrationText;
      }

      // Add multiple audio language tracks if selected
      if (audioLanguages.length > 0) {
        config.audio_languages = audioLanguages;
      }

      onSubmit(config);
    },
    [
      mode,
      topic,
      durationSeconds,
      narrationText,
      ttsProvider,
      language,
      voiceId,
      llmProvider,
      llmModel,
      audioLanguages,
      onSubmit,
      showToast,
      t,
    ]
  );

  // Convert AUDIO_DURATION_OPTIONS to CompactOption format
  const durationOptions: CompactOption[] = AUDIO_DURATION_OPTIONS.map((option) => ({
    value: option.seconds.toString(),
    label: option.label,
    disabled: option.disabled,
  }));

  // Convert LLM_PROVIDERS to CompactOption format
  const llmProviderOptions: CompactOption[] = LLM_PROVIDERS.map((provider) => ({
    value: provider.value,
    label: provider.label,
    disabled: provider.disabled,
  }));

  // Generation mode options for SegmentedPicker
  const generationModeOptions: SegmentOption[] = [
    {
      value: 'topic',
      label: t('audioConfigForm.modeTopic.label'),
      description: t('audioConfigForm.modeTopic.desc'),
    },
    {
      value: 'text',
      label: t('audioConfigForm.modeText.label'),
      description: t('audioConfigForm.modeText.desc'),
    },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="audio-config-form">
      {/* Mode Selection */}
      <SegmentedPicker
        label={t('audioConfigForm.generationMode')}
        options={generationModeOptions}
        value={mode}
        onChange={(value) => setMode(value as 'topic' | 'text')}
        size="md"
        layout="equal"
        disabled={disabled}
      />

      {/* Topic Mode Fields */}
      {mode === 'topic' && (
        <>
          <div className="form-group">
            <label htmlFor="topic" className="form-label">
              {t('audioConfigForm.topic')}
            </label>
            <input
              id="topic"
              type="text"
              className="input"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder={t('audioConfigForm.topicPlaceholder')}
              disabled={disabled}
              required
              data-testid="topic-input"
            />
          </div>

          <CompactSelect
            label={t('audioConfigForm.duration')}
            icon={<Clock className="h-4 w-4 flex-shrink-0" />}
            options={durationOptions}
            value={durationSeconds.toString()}
            onChange={(value) => setDurationSeconds(Number(value))}
            disabled={disabled}
            helpText={t('audioConfigForm.durationHelp')}
            testId="duration-select"
          />

          {/* LLM Configuration Section (Topic Mode Only) */}
          <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
            <h3 className="section-heading-sm">
              <Cpu className="h-4 w-4" />
              <span>{t('audioConfigForm.scriptGeneration')}</span>
            </h3>

            {/* Provider selector */}
            <CompactSelect
              label={t('audioConfigForm.llmProvider')}
              options={llmProviderOptions}
              value={llmProvider}
              onChange={setLlmProvider}
              disabled={disabled}
              testId="llm-provider-select"
            />

            {/* Degraded-mode banner when live model list fetch failed */}
            {modelsError && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300 text-xs">
                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                <span>{t('audioConfigForm.modelsError')}</span>
              </div>
            )}

            {/* Visual Model Selector with Avatars */}
            <LLMModelSelector
              models={availableModels}
              value={llmModel}
              onChange={setLlmModel}
              disabled={disabled || availableModels.length === 0}
              label={t('audioConfigForm.aiModel')}
              showFreeOnly={false}
            />
          </div>
        </>
      )}

      {/* Text Mode Fields */}
      {mode === 'text' && (
        <div className="form-group">
          <label htmlFor="narration-text" className="form-label">
            {t('audioConfigForm.narrationText')}
          </label>
          <textarea
            id="narration-text"
            className="input resize-y overflow-auto max-h-96"
            value={narrationText}
            onChange={(e) => setNarrationText(e.target.value)}
            placeholder={t('audioConfigForm.narrationPlaceholder')}
            disabled={disabled}
            required
            rows={20}
            minLength={USER_NARRATION_MIN_LENGTH}
            maxLength={USER_NARRATION_MAX_LENGTH}
            data-testid="narration-text-input"
          />
          <small className="block mt-1 text-xs text-white/50" data-testid="char-counter">
            {t('audioConfigForm.charCount', {
              count: narrationText.length,
              max: USER_NARRATION_MAX_LENGTH.toLocaleString(),
            })}
            {narrationText.length < USER_NARRATION_MIN_LENGTH &&
              t('audioConfigForm.minRequired', { min: USER_NARRATION_MIN_LENGTH })}
          </small>
          <p className="text-sm text-white/50 mt-1">{t('audioConfigForm.narrationHint')}</p>
        </div>
      )}

      {/* TTS Provider Selection */}
      <TTSSourceSelector
        value={ttsProvider}
        onChange={(provider) => {
          setTtsProvider(provider);
          // Drop languages unsupported by the new provider; force provider+default voice on the rest
          if (audioLanguages.length > 0) {
            const updated = audioLanguages
              .filter((config) =>
                VoiceService.isProviderAvailableForLanguage(provider, config.language)
              )
              .map((config) => ({
                ...config,
                provider,
                voice_id: VoiceService.getDefaultVoice(
                  config.language as Language,
                  provider as TTSProvider
                ),
                ref_audio_url: undefined,
              }));
            setAudioLanguages(updated);
            if (updated.length > 0) {
              setLanguage(updated[0].language);
              setVoiceId(updated[0].voice_id || '');
            } else {
              setVoiceId('');
            }
          }
        }}
        disabled={disabled}
      />

      {/* Audio Languages Section - Unified selector for all languages */}
      <div className="space-y-4 p-4 border border-white/10 rounded-lg bg-black/20">
        <div className="flex items-center justify-between">
          <h3 className="section-heading-sm">
            <Languages className="h-4 w-4" />
            <span>{t('audioConfigForm.audioLanguages')}</span>
          </h3>
          <span className="text-xs text-white/50">{t('audioConfigForm.audioLanguagesHint')}</span>
        </div>

        <div className="form-group">
          <label className="form-label text-xs text-white/50 mb-2 block">
            {t('audioConfigForm.audioLanguagesDesc')}
          </label>
          <AudioLanguageSelector
            values={audioLanguages}
            onChange={(configs) => {
              setAudioLanguages(configs);
              // Persist selected language codes to localStorage (mirrors subtitle behavior)
              persistLanguages(
                'audio',
                configs.map((c) => c.language)
              );
              // Update default language and voice based on first selection
              if (configs.length > 0) {
                const firstConfig = configs[0];
                setLanguage(firstConfig.language);
                if (firstConfig.provider) {
                  setTtsProvider(firstConfig.provider);
                }
                if (firstConfig.voice_id) {
                  setVoiceId(firstConfig.voice_id);
                }
              }
            }}
            disabled={disabled}
            className="w-full"
          />
        </div>
      </div>

      {/* Token Estimate */}
      <TokenEstimateCard
        operationPath="/api/v1/generate/tts"
        parameters={tokenEstimateParams}
        onEstimateChange={({ status }) => setCostGate(status)}
        isLocalProvider={isProviderFreeNow}
      />

      {/* Action Buttons */}
      <div className="flex justify-end gap-3">
        {/* Preview Script Button - Only show for LLM-generated mode */}
        {mode === 'topic' && onPreviewScript && (
          <button
            type="button"
            onClick={() => {
              if (!topic.trim()) return;
              const config: AudioGenerationRequest & { topic: string } = {
                mode: 'topic',
                topic: topic.trim(),
                duration_seconds: durationSeconds,
                tts_provider: ttsProvider,
                language,
                voice_id: voiceId,
                llm_provider: llmProvider,
                llm_model: llmModel,
                audio_languages: audioLanguages.length > 0 ? audioLanguages : undefined,
              };
              onPreviewScript(config);
            }}
            disabled={disabled || isPreviewLoading || !topic.trim()}
            className="btn-secondary px-6 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            data-testid="preview-script-button"
          >
            {isPreviewLoading ? (
              <>
                <div className="spinner" />
                <span>{t('audioConfigForm.generatingPreview')}</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
                <span>{t('audioConfigForm.previewScript')}</span>
              </>
            )}
          </button>
        )}

        {/* Generate Button */}
        <button
          type="submit"
          disabled={disabled || isPreviewLoading || costGate !== 'affordable'}
          className="btn-primary px-8 py-3 text-base font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          data-testid="generate-audio-button"
        >
          {disabled ? (
            <>
              <div className="spinner" />
              <span>{t('audioConfigForm.generating')}</span>
            </>
          ) : costGate !== 'affordable' ? (
            <>
              <Volume2 className="h-5 w-5" />
              <span>{costGateBlockLabel(costGate, t)}</span>
            </>
          ) : (
            <>
              <Volume2 className="h-5 w-5" />
              <span>{t('audioConfigForm.generateAudio')}</span>
            </>
          )}
        </button>
      </div>
    </form>
  );
};
